import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import { initialValues, validationSchema } from './formData';
import { CasesGeneralInformation } from '../../../../../../shared/types/modules/cases-general-information';
import { CasesGeneralInformationCallInfoProps } from './props';
import {
    ChannelBpEnum,
    ISelect,
    ParamEnum,
} from '../../../../../../shared/types/shared';
import {
    asyncGetParamByCode,
    asyncGetParamById,
    asyncGetParams,
} from '../../../../../../services/param.service';

export const useCasesGeneralInformationCallInfo = ({
    data,
    account,
    onChangeData,
    onChangeFormik,
}: CasesGeneralInformationCallInfoProps) => {
    const [channels, setChannels] = useState<ISelect[]>([]);
    const [typification1, setTypification1] = useState<ISelect[]>([]);
    const [typification2, setTypification2] = useState<ISelect[]>([]);
    const [typification3, setTypification3] = useState<ISelect[]>([]);
    const [typification4, setTypification4] = useState<ISelect[]>([]);
    const [typification5, setTypification5] = useState<ISelect[]>([]);
    const [branches, setBranches] = useState<ISelect[]>([]);
    const [rowTypification, setRowTypification] = useState<ISelect>();
    const [channelParam, setChannelParam] = useState<string>();

    const queryParams = useMemo(
        () => new URLSearchParams(location.search),
        [location.search]
    );
    const callId = queryParams.get('callId')?.trim();
    const channel = queryParams.get('channel')?.trim();

    const excludedTypifications = [
        `${ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION1}_012`,
        `${ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION1}_014`,
        `${ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION1}_026`,
        `${ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION1}_032`,
    ];
    const normalizeParamName = (value?: string) =>
        (value || '')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .trim()
            .toUpperCase();

    const isTracking =
        rowTypification?.code ===
            `${ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION1}_027` ||
        (rowTypification?.code &&
            !excludedTypifications.includes(rowTypification?.code))
            ? true
            : false;
    const isAutomobilesTypification =
        normalizeParamName(rowTypification?.name) === 'AUTOMOVILES';
    const avisoSiniestroTypification2 = typification2.find(
        (item) =>
            normalizeParamName(item.name) === 'AVISO DE SINIESTRO'
    );

    const onSubmit = (data: CasesGeneralInformation) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema: validationSchema(
            typification2.length > 0,
            typification3.length > 0,
            typification4.length > 0,
            typification5.length > 0,
            isTracking,
            isAutomobilesTypification,
            avisoSiniestroTypification2?.id as string | undefined,
            account?.userAttendanceRequired
        ),
        onSubmit,
    });
    const requireAutomobilesClaimFields =
        isAutomobilesTypification &&
        formik.values.typification2 === avisoSiniestroTypification2?.id;

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        const fetchChannel = async () => {
            if (channel) {
                if (channel === ChannelBpEnum.TELEFONIA) {
                    const param = channels.filter(
                        (item) => item?.name === 'TELEFONÍA'
                    );
                    setChannelParam(
                        param.length > 0 ? (param[0]?.id as string) : undefined
                    );
                } else {
                    const param = channels.filter(
                        (item) => item?.name === channel
                    );
                    setChannelParam(
                        param.length > 0 ? (param[0]?.id as string) : undefined
                    );
                }
            }
        };
        fetchChannel();
    }, [channel, channels]);

    useEffect(() => {
        formik?.setFieldValue('callId', (callId ? callId : data?.callId) || '');
        formik?.setFieldValue(
            'channel',
            (channelParam ? channelParam : data?.channel) || ''
        );
        formik?.setFieldValue('typification1', data?.typification1 || '');
        formik?.setFieldValue('typification2', data?.typification2 || '');
        formik?.setFieldValue('typification3', data?.typification3 || '');
        formik?.setFieldValue('typification4', data?.typification4 || '');
        formik?.setFieldValue('typification5', data?.typification5 || '');
        formik?.setFieldValue('observation', data?.observation || '');
        formik?.setFieldValue(
            'isTransferAssistance',
            data?.isTransferAssistance || false
        );
        formik?.setFieldValue('document', data?.document || '');
        formik?.setFieldValue('vehiclePlate', data?.vehiclePlate || '');
        formik?.setFieldValue('policyNumber', data?.policyNumber || '');
        formik?.setFieldValue('branch', data?.branch || '');
    }, [data, callId, channelParam]);

    useEffect(() => {
        if (Boolean(formik.values.typification1)) {
            asyncGetParams(
                ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION2,
                undefined,
                formik.values.typification1
            ).then((items) => setTypification2(items));
            asyncGetParamById(formik.values.typification1 as string).then(
                (items) => {
                    setRowTypification(items);
                }
            );
        }

        if (!data || !Boolean(data.typification1)) {
            formik.setFieldValue('typification2', '');
            formik.setFieldValue('typification3', '');
            formik.setFieldValue('typification4', '');
            formik.setFieldValue('typification5', '');
        }
        setTypification2([]);
        setTypification3([]);
        setTypification4([]);
        setTypification5([]);
    }, [formik.values.typification1]);

    useEffect(() => {
        if (Boolean(formik.values.typification2)) {
            asyncGetParams(
                ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION3,
                undefined,
                formik.values.typification2
            ).then((items) => {
                const selectedTypification1 = typification1.find(
                    (item) => item.id === formik.values.typification1
                );

                if (
                    selectedTypification1 &&
                    selectedTypification1.code ===
                        `${ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION1}_027`
                ) {
                    asyncGetParamByCode(
                        `${ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION3}_030`
                    ).then((item) => {
                        if (item) {
                            setTypification3([...items, item]);
                        } else {
                            setTypification3([...items]);
                        }
                    });
                } else {
                    setTypification3([...items]);
                }
            });
        }

        if (!data || !Boolean(data.typification2)) {
            formik.setFieldValue('typification3', '');
            formik.setFieldValue('typification4', '');
            formik.setFieldValue('typification5', '');
        }
        setTypification3([]);
        setTypification4([]);
        setTypification5([]);
    }, [formik.values.typification2]);

    useEffect(() => {
        if (Boolean(formik.values.typification3))
            asyncGetParams(
                ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION4,
                undefined,
                formik.values.typification3
            ).then((items) => setTypification4(items));

        if (!data || !Boolean(data.typification3)) {
            formik.setFieldValue('typification4', '');
            formik.setFieldValue('typification5', '');
        }
        setTypification4([]);
        setTypification5([]);
    }, [formik.values.typification3]);

    useEffect(() => {
        if (Boolean(formik.values.typification4))
            asyncGetParams(
                ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION5,
                undefined,
                formik.values.typification4
            ).then((items) => setTypification5(items));

        if (!data || !Boolean(data.typification4)) {
            formik.setFieldValue('typification5', '');
        }
        setTypification5([]);
    }, [formik.values.typification4]);

    useEffect(() => {
        asyncGetParams(ParamEnum.CASES_GENERAL_INFORMATION_CHANNEL).then(
            (params) => setChannels(params)
        );
        asyncGetParams(ParamEnum.CASES_GENERAL_INFORMATION_TYPIFICATION1).then(
            (params) => setTypification1(params)
        );
        asyncGetParams(ParamEnum.PQR_BRANCH).then((items) =>
            setBranches(items)
        );
    }, []);

    const getNameFromId = (
        idOrIds: string | ISelect | (string | ISelect)[] | undefined,
        options: ISelect[]
    ): string => {
        if (!idOrIds) return '';

        if (Array.isArray(idOrIds)) {
            return idOrIds
                .map((item) => {
                    if (typeof item === 'string') {
                        return (
                            options.find((opt) => opt.id === item)?.name ?? item
                        );
                    } else {
                        return item.name;
                    }
                })
                .join(', ');
        }

        if (typeof idOrIds === 'string') {
            return options.find((opt) => opt.id === idOrIds)?.name ?? idOrIds;
        }

        return idOrIds.name;
    };

    useEffect(() => {
        const tipification1Name = getNameFromId(
            formik.values.typification1,
            typification1
        );
        const tipification2Name = getNameFromId(
            formik.values.typification2,
            typification2
        );
        const nameParts = [tipification1Name, tipification2Name].filter(
            (part) => part
        );

        const typification = nameParts.join(' | ');
        if (typification) formik.setFieldValue('typification', typification);
    }, [
        typification1,
        typification2,
        formik.values.typification1,
        formik.values.typification2,
    ]);

    return {
        formik,
        channels,
        typification1,
        typification2,
        typification3,
        typification4,
        typification5,
        branches,
        isTracking,
        requireAutomobilesClaimFields,
    };
};
